import { ArrowLeft, FileText, CreditCard, AlertTriangle, XCircle, Scale, RefreshCw, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const lastUpdated = "January 29, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-full">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Terms of Service</h1>
              <p className="text-gray-500">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed">
              Welcome to Radiant 40! These Terms of Service ("Terms") govern your use of the Radiant 40 application and services operated by Sundog Rosie ("we," "our," or "us").
            </p>

            <div className="bg-primary/5 rounded-xl p-6 my-8">
              <p className="text-gray-700 font-medium m-0">
                By accessing or using Radiant 40, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our services.
              </p>
            </div>

            {/* Description of Service */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">1. Description of Service</h2>
            <p className="text-gray-700">
              Radiant 40 is a 12-week digital wellness program designed for women in their 40s. The service includes daily tasks, guided content, progress tracking, community features, and gamification elements. The content is for informational and educational purposes only and is not medical advice.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 my-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-1" />
                <p className="text-amber-800 m-0">
                  <strong>Health Disclaimer:</strong> Radiant 40 is not a substitute for professional medical advice, diagnosis, or treatment. Always consult with a qualified healthcare provider before starting any wellness program, especially if you have existing health conditions.
                </p>
              </div>
            </div>

            {/* Account Registration */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">2. Account Registration</h2>
            <p className="text-gray-700">To use Radiant 40, you must:</p>
            <ul className="text-gray-700">
              <li>Be at least 18 years old</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>

            {/* Subscription and Payment */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <CreditCard className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">3. Subscription and Payment</h2>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">Pricing</h3>
            <p className="text-gray-700">
              We offer the following subscription options:
            </p>
            <ul className="text-gray-700">
              <li><strong>Monthly Plan:</strong> $9.99 per 4-week period</li>
              <li><strong>Full Course:</strong> $24.99 one-time payment for all 12 weeks</li>
            </ul>
            <p className="text-gray-700">
              Prices may vary by platform and location. The price displayed at checkout is the final price.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">Payment Processing</h3>
            <p className="text-gray-700">
              Payments are processed through secure third-party payment processors:
            </p>
            <ul className="text-gray-700">
              <li><strong>Website:</strong> Stripe</li>
              <li><strong>iOS App:</strong> Apple In-App Purchase</li>
              <li><strong>Android App:</strong> Google Play Billing</li>
              <li><strong>Amazon:</strong> Amazon In-App Purchasing</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">Subscription Renewal</h3>
            <p className="text-gray-700">
              Monthly subscriptions automatically renew until canceled. You will be charged the applicable fee at the beginning of each billing period. You may cancel at any time through your account settings or the respective app store.
            </p>

            {/* Refund Policy */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <RefreshCw className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">4. Refund Policy</h2>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6 my-6">
              <p className="text-green-800 font-medium m-0">
                <strong>7-Day Satisfaction Guarantee:</strong> If you are not satisfied with Radiant 40, you may request a full refund within 7 days of your initial purchase. After 7 days, all sales are final and no refunds will be issued.
              </p>
            </div>

            <p className="text-gray-700">
              <strong>How to request a refund:</strong>
            </p>
            <ul className="text-gray-700">
              <li><strong>Website purchases:</strong> Email sundog.rose@gmail.com within 7 days</li>
              <li><strong>Apple App Store:</strong> Request through Apple's refund process</li>
              <li><strong>Google Play:</strong> Request through Google Play's refund process</li>
              <li><strong>Amazon:</strong> Request through Amazon's refund process</li>
            </ul>
            <p className="text-gray-700">
              App store refunds are subject to the respective platform's refund policies, which may differ from ours.
            </p>

            {/* Cancellation */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">5. Cancellation</h2>
            <p className="text-gray-700">
              You may cancel your subscription at any time:
            </p>
            <ul className="text-gray-700">
              <li><strong>Website:</strong> Through your Account Settings page</li>
              <li><strong>iOS:</strong> Settings → Apple ID → Subscriptions → Radiant 40</li>
              <li><strong>Android:</strong> Google Play → Subscriptions → Radiant 40</li>
              <li><strong>Amazon:</strong> Amazon Appstore → Subscriptions</li>
            </ul>
            <p className="text-gray-700">
              Cancellation takes effect at the end of your current billing period. You will retain access until then.
            </p>

            {/* User Conduct */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <XCircle className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">6. User Conduct</h2>
            </div>
            <p className="text-gray-700">You agree NOT to:</p>
            <ul className="text-gray-700">
              <li>Violate any applicable laws or regulations</li>
              <li>Post harmful, offensive, or inappropriate content</li>
              <li>Harass, bully, or intimidate other users</li>
              <li>Share your account credentials with others</li>
              <li>Attempt to access other users' accounts</li>
              <li>Use the service for any commercial purpose without authorization</li>
              <li>Copy, distribute, or modify our content without permission</li>
              <li>Interfere with the proper functioning of the service</li>
            </ul>
            <p className="text-gray-700">
              We reserve the right to terminate accounts that violate these terms.
            </p>

            {/* Intellectual Property */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">7. Intellectual Property</h2>
            <p className="text-gray-700">
              All content in Radiant 40, including text, graphics, logos, images, and software, is the property of Sundog Rosie and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
            </p>
            <p className="text-gray-700">
              You retain ownership of content you create (posts, photos), but grant us a license to display it within the service.
            </p>

            {/* Disclaimer of Warranties */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Scale className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">8. Disclaimer of Warranties</h2>
            </div>
            <p className="text-gray-700">
              RADIANT 40 IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. YOUR USE OF THE SERVICE IS AT YOUR OWN RISK.
            </p>

            {/* Limitation of Liability */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">9. Limitation of Liability</h2>
            <p className="text-gray-700">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SUNDOG ROSIE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE PAST 12 MONTHS.
            </p>

            {/* Indemnification */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">10. Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify and hold harmless Sundog Rosie from any claims, damages, or expenses arising from your use of the service, violation of these Terms, or infringement of any rights of another.
            </p>

            {/* Termination */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">11. Termination</h2>
            <p className="text-gray-700">
              We may terminate or suspend your account at any time for violation of these Terms. You may delete your account at any time through Account Settings. Upon termination, your right to use the service ceases immediately.
            </p>

            {/* Governing Law */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">12. Governing Law</h2>
            <p className="text-gray-700">
              These Terms shall be governed by the laws of the United States. Any disputes shall be resolved in the courts of the United States.
            </p>

            {/* Changes to Terms */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">13. Changes to Terms</h2>
            <p className="text-gray-700">
              We may update these Terms from time to time. We will notify you of material changes by posting the new Terms and updating the "Last updated" date. Continued use after changes constitutes acceptance.
            </p>

            {/* Contact */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Mail className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">14. Contact Us</h2>
            </div>
            <p className="text-gray-700">
              If you have questions about these Terms, please contact us at:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 mt-4">
              <p className="text-gray-700 m-0">
                <strong>Sundog Rosie</strong><br />
                Email: <a href="mailto:sundog.rose@gmail.com" className="text-primary hover:underline">sundog.rose@gmail.com</a><br />
                United States
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mt-8 text-gray-500">
          © 2026 Sundog Rosie. All rights reserved.
        </div>
      </div>
    </div>
  );
}
